import { computed, inject, Injectable, Signal } from '@angular/core';
import { DamageRoles, Roles } from '../models/roles';
import { isLegacyVisibleScreen, Permission } from '../models/permissions';
import { UserStore } from '../store/user.store';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private userStore = inject(UserStore);

  /**
   * Returns a Signal<boolean> that resolves to true when the user holds ALL of
   * the given permissions. Works for BOTH legacy and new-system users:
   *
   *   New-system user (effective_permissions non-empty):
   *     - Admin bypass: effective_roles includes 'system_admin' or 'admin'
   *     - Permission check: against effective_permissions
   *
   *   Legacy user (effective_permissions empty):
   *     - Admin bypass: dmg_groups includes 'dmg_admin'
   *     - Permission check: against dmg_permissions
   *
   * After migration cutover: delete the legacy branch and the Roles import.
   */
  hasPermissions(permissions: Permission[]): Signal<boolean> {
    return computed(() => {
      if (!permissions.length) return true;

      // Title-level override is authoritative: a restricted permission is denied for
      // everyone, including admins — this intentionally beats the admin bypass below.
      const restricted = this.userStore.titlePermissionRestrictions();
      if (restricted.length && permissions.some(perm => restricted.includes(perm as string))) {
        return false;
      }

      if (this.userStore.isNewPermissionSystem()) {
        // ── NEW PATH — RH_*_Prod groups ──────────────────────────────────────
        const roles = this.userStore.effectiveRoles();
        if (roles.includes(DamageRoles.SYSTEM_ADMIN) || roles.includes(DamageRoles.ADMIN)) {
          return true;
        }
        // Check effective_permissions (repairhub:* strings) for new-format callers.
        // Also check dmg_permissions as a fallback: the backend fills it with both
        // old AND new strings for new-system users (permissions_with_legacy_aliases),
        // so old-format templates ('damage_review_screen' etc.) still resolve correctly.
        const ep = this.userStore.user()?.effective_permissions || [];
        const dp = this.userStore.user()?.dmg_permissions || [];
        return permissions.some(perm => ep.includes(perm) || dp.includes(perm));
      } else {
        // ── LEGACY PATH — default (grp_OKTA_DMG_* groups) ───────────────────
        // DELETE this branch after migration cutover.
        const userGroups = this.userStore.user()?.dmg_groups || [];
        if (userGroups.includes(Roles.DAMAGE_ADMIN)) {
          return true;
        }
        // Base operational screens stay visible to every recognized legacy user
        // (Legacy All) — they carry no repairhub:screen:* strings during parallel run.
        if (permissions.some(perm => isLegacyVisibleScreen(perm as string))) {
          return true;
        }
        const userPermissions = this.userStore.user()?.dmg_permissions || [];
        return permissions.some(perm => userPermissions.includes(perm));
      }
    });
  }
}
